import { useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import dayjs from "dayjs";

import { dbAML as db } from "../../../../backend/firebaseConfig";
import {
  getCheckPrice,
  formatGBP,
  normaliseCheckType,
} from "../../../Accounts/AMLStatements.js/Utils/checkPricing";
import "../../../../css/Finance/AML/amlDashboard.css";
import "../../../../css/Praplanning/ParaplanningCasesTable.css";

const EXPERIAN_SUBCOLLECTION_NAMES = [
  "ExperianResponse",
  "Experian Response",
  "experianResponse",
  "experian response",
];

const REQUEST_DATA_SUBCOLLECTION_NAMES = [
  "Request Data",
  "RequestData",
  "requestData",
  "request data",
];

const RETENTION_MONTH_COUNT = 2;
const REQUIRE_STAFF_CREATED_ONLY = false;
const STAFF_EMAIL_DOMAIN = "@rosemountfs.co.uk";

const normaliseEmail = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const clean = (value) => String(value || "").trim();

const toJsDate = (value) => {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    const converted = value.toDate();
    return Number.isNaN(converted?.getTime?.()) ? null : converted;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string" || typeof value === "number") {
    const converted = new Date(value);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  if (typeof value?.seconds === "number") {
    const converted = new Date(value.seconds * 1000);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  return null;
};

const formatDate = (value) => {
  const date = toJsDate(value);
  return date ? dayjs(date).format("D MMMM YYYY") : "-";
};

const getLastNMonths = (count = RETENTION_MONTH_COUNT) => {
  const now = new Date();

  return Array.from({ length: count }, (_, index) => {
    const date = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - index, 1),
    );

    const value = `${date.getUTCFullYear()}-${String(
      date.getUTCMonth() + 1,
    ).padStart(2, "0")}`;

    return {
      value,
      label: dayjs(`${value}-01`).format("MMMM YYYY"),
      shortLabel: dayjs(`${value}-01`).format("MMM YYYY"),
      isCurrentMonth: index === 0,
    };
  });
};

const getMonthKey = (value) => {
  const date = toJsDate(value);
  return date ? dayjs(date).format("YYYY-MM") : "";
};

const getRequestDataForCheck = async (checkDocRef) => {
  if (!checkDocRef) return {};

  for (const name of REQUEST_DATA_SUBCOLLECTION_NAMES) {
    try {
      const subcollectionRef = collection(checkDocRef, name);
      const snapshot = await getDocs(query(subcollectionRef, limit(1)));

      if (!snapshot.empty) {
        return snapshot.docs[0].data() || {};
      }
    } catch {
      // Try next possible subcollection name.
    }
  }

  return {};
};

const getForAdviserEmailOnly = (record) => {
  const forAdviser = record?.forAdviser;

  if (!forAdviser) return "";

  if (typeof forAdviser === "string") {
    return clean(forAdviser);
  }

  if (typeof forAdviser === "object") {
    return clean(
      forAdviser.email ||
        forAdviser.adviserEmail ||
        forAdviser.userEmail ||
        forAdviser.workEmail ||
        "",
    );
  }

  return "";
};

const getOnBehalfOfEmail = (record) => {
  return clean(
    getForAdviserEmailOnly(record) ||
      record?.forAdviserEmail ||
      record?.requestedForEmail ||
      record?.submittedForEmail ||
      record?.submittedOnBehalfOfEmail ||
      record?.adviserEmail ||
      record?.clientAdviserEmail ||
      "",
  );
};

const getRequestedByEmail = (record) => {
  return clean(
    record?.createdByEmail ||
      record?.createdBy ||
      record?.requestedByEmail ||
      record?.submittedByEmail ||
      record?.adminEmail ||
      record?.userEmail ||
      record?.__ownerEmail ||
      "",
  );
};

const isStaffEmail = (email) => {
  return normaliseEmail(email).endsWith(STAFF_EMAIL_DOMAIN);
};

const isStaffCreatedCheck = (record) => {
  const createdByEmail = getRequestedByEmail(record);

  const createdByRole = String(
    record?.createdByRole || record?.requestedByRole || "",
  )
    .trim()
    .toLowerCase();

  return (
    isStaffEmail(createdByEmail) ||
    createdByRole.includes("admin") ||
    createdByRole.includes("staff")
  );
};

const checkBelongsToAdviser = (record, adviserEmail) => {
  const targetEmail = normaliseEmail(adviserEmail);
  if (!targetEmail) return false;

  const forAdviser = record?.forAdviser || {};

  const possibleEmails = [
    record?.__ownerEmail,
    record?.ownerEmail,
    record?.userEmail,
    getOnBehalfOfEmail(record),
    getForAdviserEmailOnly(record),
    record?.adviserEmail,
    record?.forAdviserEmail,
    record?.requestedForEmail,
    record?.submittedForEmail,
    record?.submittedOnBehalfOfEmail,
    record?.clientAdviserEmail,
    typeof forAdviser === "string" ? forAdviser : "",
    forAdviser?.email,
    forAdviser?.adviserEmail,
    forAdviser?.userEmail,
    forAdviser?.workEmail,
  ].map(normaliseEmail);

  return possibleEmails.includes(targetEmail);
};

const hasSessionId = (record) => {
  return (
    record?.sessionId !== undefined &&
    record?.sessionId !== null &&
    String(record.sessionId).trim() !== ""
  );
};

const isSimpleOrPepsCheck = (record) => {
  const checkType = String(record?.checkType || "").toLowerCase();
  return checkType.includes("simple") || checkType.includes("pep");
};

const hasSubcollection = async (docRef, subcollectionNames) => {
  if (!docRef) return false;

  for (const name of subcollectionNames) {
    try {
      const subcollectionRef = collection(docRef, name);
      const snapshot = await getDocs(query(subcollectionRef, limit(1)));

      if (!snapshot.empty) return true;
    } catch {
      // Try next possible subcollection name.
    }
  }

  return false;
};

const shouldIncludeCheck = async (check) => {
  if (isSimpleOrPepsCheck(check)) return true;
  if (hasSessionId(check)) return true;

  return hasSubcollection(check.__ref, EXPERIAN_SUBCOLLECTION_NAMES);
};

const getStatusLabel = (record) => {
  const status = String(
    record?.status ||
      record?.decision ||
      record?.result ||
      record?.overallStatus ||
      "",
  )
    .trim()
    .toLowerCase();

  if (["approved", "pass", "passed", "completed", "clear"].includes(status)) {
    return "Approved";
  }

  if (["referred", "refer", "review", "needs_review"].includes(status)) {
    return "Referred";
  }

  if (["failed", "declined", "rejected", "fail"].includes(status)) {
    return "Declined";
  }

  if (["pending", "processing", "submitted", "new"].includes(status)) {
    return "In Progress";
  }

  return status
    ? status
        .replaceAll("_", " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : "Unknown";
};

const getClientName = (record) => {
  const requestData = record?.__requestData || {};
  const personal =
    requestData?.personal ||
    requestData?.requestData?.personal ||
    requestData?.input?.personal ||
    {};

  const requestDataName = [
    personal?.firstName,
    personal?.middleName,
    personal?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    requestDataName ||
    clean(record?.clientName) ||
    clean(record?.fullName) ||
    clean(record?.applicantName) ||
    clean(record?.name) ||
    [record?.firstName, record?.lastName].filter(Boolean).join(" ").trim() ||
    "AML Check"
  );
};

const getUserPermissions = () => {
  try {
    return JSON.parse(localStorage.getItem("userPermissions")) || [];
  } catch {
    return [];
  }
};

const hasAdminCostPermission = (permissions = []) => {
  return permissions.some((permission) => {
    const value = String(permission || "").toLowerCase();

    return (
    //   value.includes("admin") ||
      value.includes("administrator")
    );
  });
};

export default function AdviserAMLTab({ adviserEmail }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");

  const months = useMemo(() => getLastNMonths(), []);
  const [activeMonth, setActiveMonth] = useState(months[0]?.value || "");

  const canShowCost = useMemo(() => {
    return hasAdminCostPermission(getUserPermissions());
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchAMLChecks = async () => {
      const email = normaliseEmail(adviserEmail);

      if (!email) {
        setRows([]);
        return;
      }

      try {
        setLoading(true);
        setErrorText("");

        const checksQuery = query(
          collectionGroup(db, "Checks"),
          orderBy("createdAt", "desc"),
        );

        const snapshot = await getDocs(checksQuery);
        const allowedMonthKeys = new Set(months.map((month) => month.value));

        const rawRows = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          const ownerEmail = docSnap.ref.parent?.parent?.id || "";
          const checkType = normaliseCheckType(data.checkType);
          const price = getCheckPrice(checkType);
          const createdAt =
            data.createdAt || data.submittedAt || data.updatedAt;
          const monthKey = getMonthKey(createdAt);

          return {
            ...data,
            id: docSnap.id,
            ref: docSnap.ref,
            __ref: docSnap.ref,
            __ownerEmail: ownerEmail,
            __checkTypeNorm: checkType,
            __price: price,
            __createdAt: createdAt,
            __monthKey: monthKey,
          };
        });

        const adviserRows = rawRows.filter((row) => {
          if (!allowedMonthKeys.has(row.__monthKey)) return false;

          const belongsToAdviser = checkBelongsToAdviser(row, email);
          if (!belongsToAdviser) return false;

          if (REQUIRE_STAFF_CREATED_ONLY) {
            return isStaffCreatedCheck(row);
          }

          return true;
        });

        const includeResults = await Promise.all(
          adviserRows.map(async (row) => {
            const include = await shouldIncludeCheck(row);
            if (!include) return null;

            const requestData = await getRequestDataForCheck(row.__ref);

            return {
              ...row,
              __requestData: requestData,
            };
          }),
        );

        const matchedRows = includeResults.filter(Boolean).map((row) => ({
          id: row.id,
          clientName: getClientName(row),
          checkType: row.__checkTypeNorm || "UNKNOWN",
          status: getStatusLabel(row),
          createdAt: row.__createdAt || row.createdAt || null,
          monthKey: row.__monthKey,
          requestedByEmail: getRequestedByEmail(row),
          onBehalfOfEmail: getOnBehalfOfEmail(row),
          ownerEmail: row.__ownerEmail,
          price: Number(row.__price || 0),
          isStaffCreated: isStaffCreatedCheck(row),
        }));

        if (!cancelled) {
          setRows(matchedRows);
        }
      } catch (error) {
        console.error("Failed to load adviser AML checks:", error);

        if (!cancelled) {
          setRows([]);
          setErrorText("Failed to load AML checks.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchAMLChecks();

    return () => {
      cancelled = true;
    };
  }, [adviserEmail, months]);

  const visibleRows = useMemo(() => {
    return rows.filter((row) => row.monthKey === activeMonth);
  }, [rows, activeMonth]);

  const activeMonthDetails = useMemo(() => {
    return months.find((month) => month.value === activeMonth) || months[0];
  }, [months, activeMonth]);

  const totals = useMemo(() => {
    return visibleRows.reduce(
      (acc, row) => {
        const status = String(row.status || "").toLowerCase();

        acc.total += 1;
        acc.amount += Number(row.price || 0);

        if (status === "approved") acc.approved += 1;
        else if (status === "declined") acc.declined += 1;
        else if (status === "referred") acc.referred += 1;
        else acc.open += 1;

        return acc;
      },
      {
        total: 0,
        open: 0,
        approved: 0,
        declined: 0,
        referred: 0,
        amount: 0,
      },
    );
  }, [visibleRows]);

  return (
    <div className="adviser-aml-tab">
      <div className="adviser-aml-month-tabs" role="tablist">
        {months.map((month) => {
          const isActive = activeMonth === month.value;

          return (
            <button
              key={month.value}
              type="button"
              className={`aml-hero-month-pill dark${isActive ? " active" : ""}`}
            //   onClick={() => setSelectedMonth(month.value)}
              aria-pressed={isActive}
              //   key={month.value}
              //   type="button"
              //   role="tab"
              //   aria-selected={isActive}
              //   className={`adviser-aml-month-tab ${isActive ? "is-active" : ""}`}
                onClick={() => setActiveMonth(month.value)}
            >
              <span>{month.shortLabel}</span>
              {month.isCurrentMonth && <small>Current</small>}
            </button>
          );
        })}
      </div>

      <div className="adviser-aml-summary-grid">
        <div className="adviser-aml-summary-card adviser-aml-summary-card-cream">
          <span>Total Checks</span>
          <strong>{totals.total}</strong>
          <p>{activeMonthDetails?.label || "Selected month"}</p>
        </div>

        {/* <div className="adviser-aml-summary-card adviser-aml-summary-card-cream">
          <span>Open / Referred</span>
          <strong>{totals.open + totals.referred}</strong>
          <p>Needs review or in progress</p>
        </div> */}

        {canShowCost && (
          <div className="adviser-aml-summary-card adviser-aml-summary-card-green">
            <span>Total Cost</span>
            <strong>{formatGBP(totals.amount)}</strong>
            <p>Visible to admins only</p>
          </div>
        )}
      </div>

      <div className="adviser-aml-card">
        <div className="adviser-aml-card-head">
          <div>
            <span className="adviser-aml-label" style={{textTransform:"lowercase"}}>check²</span>
            <p>
              Shows AML checks matched to this adviser for{" "}
              {activeMonthDetails?.label || "the selected month"}.
            </p>
          </div>
        </div>

        {errorText ? (
          <div className="adviser-aml-error">{errorText}</div>
        ) : null}

        {loading ? (
          <div className="adviser-aml-empty">Loading AML checks...</div>
        ) : visibleRows.length === 0 ? (
          <div className="adviser-aml-empty">
            No AML checks found for this adviser in{" "}
            {activeMonthDetails?.label || "this month"}.
          </div>
        ) : (
          <div className="ParaplanningTableWrap">
            <table className="ParaplanningTable">
              <thead>
                <tr>
                  {[
                    "Client Name",
                    "Check Type",
                    "Created",
                    "Requested By",
                    ...(canShowCost
                      ? ["Cost (Visible to admins only)"]
                      : []),
                  ].map((label) => (
                    <th key={label}>
                      <span className="ParaplanningTableHeaderButton">
                        <span className="FP_Content">{label}</span>
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {visibleRows.map((row) => (
                  <tr key={`${row.ownerEmail}-${row.id}`}>
                    <td>
                      <div className="ParaplanningTableCell adviser-aml-client-cell">
                        <span className="ParaplanningTableCell__Main FP_Content black">
                          {row.clientName}
                        </span>

                        {row.isStaffCreated && (
                          <span className="adviser-aml-staff-pill">
                            Staff requested
                          </span>
                        )}
                      </div>
                    </td>

                    <td>
                      <div className="ParaplanningTableCell">
                        <span className="ParaplanningTableCell__Main FP_Content black">
                          {row.checkType}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="ParaplanningTableCell">
                        <span className="ParaplanningTableCell__Main FP_Content black">
                          {formatDate(row.createdAt)}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="ParaplanningTableCell adviser-aml-created-by-cell">
                        <span className="ParaplanningTableCell__Main FP_Content black">
                          {row.requestedByEmail || "-"}
                        </span>
                      </div>
                    </td>

                    {canShowCost && (
                      <td>
                        <div className="ParaplanningTableCell">
                          <span className="ParaplanningTableCell__Main ParaplanningTableCell__Main--Money FP_Content black">
                            {formatGBP(row.price)}
                          </span>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
